import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getClient, getClientSummary, getClientTechnicians, getClientDevices,
    getAlerts, getDevices, assignDeviceToClient, updateClient
} from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
    Monitor, AlertTriangle, Shield, Users, ArrowLeft, Pencil, Plus,
    FileText, CheckCircle, Clock
} from 'lucide-react';

const TABS = ['Overview', 'Devices', 'Alerts', 'Compliance', 'Reports'];

export const MSPClientDetailPage = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [summary, setSummary] = useState(null);
    const [technicians, setTechnicians] = useState([]);
    const [devices, setDevices] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [activeTab, setActiveTab] = useState('Overview');
    const [loading, setLoading] = useState(true);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [unassignedDevices, setUnassignedDevices] = useState([]);
    const [assigningDeviceId, setAssigningDeviceId] = useState(null);

    useEffect(() => {
        fetchClientData();
    }, [clientId]);

    useEffect(() => {
        if (activeTab === 'Devices') fetchDevices();
        if (activeTab === 'Alerts') fetchAlerts();
    }, [activeTab, clientId]);

    const fetchClientData = async () => {
        try {
            const [clientRes, summaryRes] = await Promise.all([
                getClient(clientId),
                getClientSummary(clientId).catch(() => null),
            ]);
            setClient(clientRes);
            setSummary(summaryRes);
            // Also fetch technicians
            try {
                const techRes = await getClientTechnicians(clientId);
                setTechnicians(Array.isArray(techRes) ? techRes : techRes.items || []);
            } catch { setTechnicians([]); }
        } catch (err) {
            toast.error('Failed to load client data');
        } finally {
            setLoading(false);
        }
    };

    const fetchDevices = async () => {
        try {
            const res = await getClientDevices(clientId);
            setDevices(Array.isArray(res) ? res : res.items || []);
        } catch { setDevices([]); }
    };

    const fetchAlerts = async () => {
        try {
            const res = await getAlerts(1, 100, clientId);
            setAlerts(res.items || []);
        } catch { setAlerts([]); }
    };

    const openAssignDialog = async () => {
        try {
            const allDevices = await getDevices(1, 100);
            const items = allDevices.items || [];
            const assigned = new Set(devices.map(d => d.id));
            setUnassignedDevices(items.filter(d => !assigned.has(d.id) && !d.client_id));
        } catch { setUnassignedDevices([]); }
        setAssignDialogOpen(true);
    };

    const handleAssignDevice = async (deviceId) => {
        setAssigningDeviceId(deviceId);
        try {
            await assignDeviceToClient(clientId, deviceId);
            toast.success('Device assigned successfully');
            setAssignDialogOpen(false);
            fetchDevices();
            fetchClientData();
        } catch (err) {
            toast.error('Failed to assign device');
        } finally {
            setAssigningDeviceId(null);
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return 'bg-red-100 text-red-800 border-red-200';
            case 'warning': return 'bg-amber-100 text-amber-800 border-amber-200';
            default: return 'bg-blue-100 text-blue-800 border-blue-200';
        }
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${Math.floor(diffHours / 24)}d ago`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="client-detail-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!client) {
        return (
            <div className="text-center py-16">
                <p className="text-muted-foreground">Client not found</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/msp/clients')}>
                    Back to Clients
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="msp-client-detail-page">
            {/* Back Button */}
            <Button variant="ghost" className="p-0 h-auto text-sm text-muted-foreground" onClick={() => navigate('/msp/clients')}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Clients
            </Button>

            {/* Client Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
                        {client.name}
                    </h1>
                    <div className="flex items-center gap-3 mt-2">
                        {client.industry && (
                            <Badge variant="secondary">{client.industry}</Badge>
                        )}
                        {client.contact_name && (
                            <span className="text-sm text-muted-foreground">
                                Contact: {client.contact_name}
                                {client.contact_email && ` (${client.contact_email})`}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Tab Bar */}
            <div className="border-b border-border">
                <div className="flex gap-0">
                    {TABS.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'Overview' && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Card className="border-border shadow-sm">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">TOTAL DEVICES</CardTitle>
                                <Monitor className="w-4 h-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="font-heading text-3xl font-bold">{summary?.total_devices || client.device_count || 0}</div>
                            </CardContent>
                        </Card>
                        <Card className="border-border shadow-sm">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">ONLINE DEVICES</CardTitle>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                            </CardHeader>
                            <CardContent>
                                <div className="font-heading text-3xl font-bold text-green-600">{summary?.online_devices || 0}</div>
                            </CardContent>
                        </Card>
                        <Card className="border-border shadow-sm">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">OPEN ALERTS</CardTitle>
                                <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="font-heading text-3xl font-bold">{summary?.open_alerts || 0}</div>
                            </CardContent>
                        </Card>
                        <Card className="border-border shadow-sm">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">CRITICAL ALERTS</CardTitle>
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                            </CardHeader>
                            <CardContent>
                                <div className="font-heading text-3xl font-bold text-red-600">{summary?.critical_alerts || 0}</div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Client Portal Access */}
                    {client.contact_email && (
                        <Card className="border-border shadow-sm">
                            <CardHeader>
                                <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                                    <Shield className="w-5 h-5" />
                                    Client Portal Access
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                    <div>
                                        <p className="text-sm font-medium">{client.contact_email}</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {client.password_status === 'active'
                                                ? `Password set on ${new Date(client.password_changed_at).toLocaleDateString()}`
                                                : 'Using temporary password — waiting for client to set their own'}
                                        </p>
                                    </div>
                                    <Badge variant={client.password_status === 'active' ? 'default' : 'secondary'}>
                                        {client.password_status === 'active' ? 'Active' : 'Pending'}
                                    </Badge>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Assigned Technicians */}
                    <Card className="border-border shadow-sm">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                Assigned Technicians
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {technicians.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No technicians assigned to this client.</p>
                            ) : (
                                <div className="space-y-2">
                                    {technicians.map((tech) => (
                                        <div key={tech.id || tech.user_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                                                {tech.name?.[0]?.toUpperCase() || 'T'}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium">{tech.name || tech.email}</p>
                                                <p className="text-xs text-muted-foreground">{tech.email}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === 'Devices' && (
                <div className="space-y-4">
                    <div className="flex justify-end">
                        <Button onClick={openAssignDialog}>
                            <Plus className="w-4 h-4 mr-2" />
                            Assign Device
                        </Button>
                    </div>
                    {devices.length === 0 ? (
                        <Card className="border-border shadow-sm">
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <Monitor className="w-10 h-10 text-muted-foreground mb-3" />
                                <p className="text-sm text-muted-foreground">No devices assigned to this client</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="border-border shadow-sm">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>OS</TableHead>
                                        <TableHead>Last Seen</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {devices.map((device) => (
                                        <TableRow key={device.id}>
                                            <TableCell className="font-medium">{device.hostname || device.name}</TableCell>
                                            <TableCell>{device.device_type || device.type || '-'}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`status-dot ${device.status === 'online' ? 'status-online' : 'status-offline'}`}></span>
                                                    {device.status || 'unknown'}
                                                </div>
                                            </TableCell>
                                            <TableCell>{device.os || '-'}</TableCell>
                                            <TableCell className="text-muted-foreground text-sm">
                                                {device.last_seen ? formatTimeAgo(device.last_seen) : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Card>
                    )}

                    {/* Assign Device Dialog */}
                    <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Assign Device to {client.name}</DialogTitle>
                            </DialogHeader>
                            <div className="py-4 max-h-[400px] overflow-y-auto">
                                {unassignedDevices.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-4">No unassigned devices available</p>
                                ) : (
                                    <div className="space-y-2">
                                        {unassignedDevices.map((device) => (
                                            <div key={device.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                                <div>
                                                    <p className="text-sm font-medium">{device.hostname || device.name}</p>
                                                    <p className="text-xs text-muted-foreground">{device.device_type || device.type} - {device.os || 'N/A'}</p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleAssignDevice(device.id)}
                                                    disabled={assigningDeviceId === device.id}
                                                >
                                                    {assigningDeviceId === device.id ? 'Assigning...' : 'Assign'}
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Close</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            )}

            {activeTab === 'Alerts' && (
                <div>
                    {alerts.length === 0 ? (
                        <Card className="border-border shadow-sm">
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <CheckCircle className="w-10 h-10 text-muted-foreground mb-3" />
                                <p className="text-sm text-muted-foreground">No alerts for this client</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="border-border shadow-sm">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Title</TableHead>
                                        <TableHead>Severity</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Created</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {alerts.map((alert) => (
                                        <TableRow key={alert.id}>
                                            <TableCell className="font-medium">{alert.title}</TableCell>
                                            <TableCell>
                                                <Badge className={`${getSeverityColor(alert.severity)} text-xs`}>
                                                    {alert.severity}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>{alert.status}</TableCell>
                                            <TableCell className="text-muted-foreground text-sm">
                                                {formatTimeAgo(alert.created_at)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Card>
                    )}
                </div>
            )}

            {activeTab === 'Compliance' && (
                <Card className="border-border shadow-sm">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Shield className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-lg font-medium text-foreground mb-2">
                            Compliance tracking for {client.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Per-client compliance tracking is coming soon.
                        </p>
                    </CardContent>
                </Card>
            )}

            {activeTab === 'Reports' && (
                <Card className="border-border shadow-sm">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-lg font-medium text-foreground mb-2">Security Reports</p>
                        <p className="text-sm text-muted-foreground mb-4">
                            Export a security report for {client.name}
                        </p>
                        <Button variant="outline" disabled>
                            <FileText className="w-4 h-4 mr-2" />
                            Export Security Report (Coming Soon)
                        </Button>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default MSPClientDetailPage;
